import React, { useState, useEffect } from 'react';
import Layout from '../components/Layout';
import { useAuth } from '../contexts/AuthContext';
import { useLanguage } from '../contexts/LanguageContext';
import { Palette, Save, RotateCcw, Check, Eye } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';

// `name` reste l'identifiant stable (clé React + data-testid) ; `labelKey` sert à l'affichage i18n.
const PRESETS = [
  { name: 'Rouge & Jaune', labelKey: 'themeSettings.presetRougeJaune', primary: '#dc2626', secondary: '#eab308', accent: '#000000' },
  { name: 'Guyane', labelKey: 'themeSettings.presetGuyane', primary: '#009b3a', secondary: '#fedd00', accent: '#ef0000' },
  { name: 'Ocean Bleu', labelKey: 'themeSettings.presetOceanBleu', primary: '#2563eb', secondary: '#06b6d4', accent: '#0f172a' },
  { name: 'Royal Violet', labelKey: 'themeSettings.presetRoyalViolet', primary: '#7c3aed', secondary: '#a78bfa', accent: '#1e1b4b' },
  { name: 'Emeraude', labelKey: 'themeSettings.presetEmeraude', primary: '#059669', secondary: '#34d399', accent: '#064e3b' },
  { name: 'Sunset', labelKey: 'themeSettings.presetSunset', primary: '#ea580c', secondary: '#f59e0b', accent: '#1c1917' },
];

// `key` = nom de propriété du thème (backend) + data-testid : NE PAS traduire. Affichage via labelKey/descKey.
const COLOR_FIELDS = [
  { key: 'primary', labelKey: 'themeSettings.fieldPrimaryLabel', descKey: 'themeSettings.fieldPrimaryDesc' },
  { key: 'secondary', labelKey: 'themeSettings.fieldSecondaryLabel', descKey: 'themeSettings.fieldSecondaryDesc' },
  { key: 'accent', labelKey: 'themeSettings.fieldAccentLabel', descKey: 'themeSettings.fieldAccentDesc' },
  { key: 'sidebar_bg', labelKey: 'themeSettings.fieldSidebarBgLabel', descKey: 'themeSettings.fieldSidebarBgDesc' },
  { key: 'sidebar_text', labelKey: 'themeSettings.fieldSidebarTextLabel', descKey: 'themeSettings.fieldSidebarTextDesc' },
  { key: 'header_bg', labelKey: 'themeSettings.fieldHeaderBgLabel', descKey: 'themeSettings.fieldHeaderBgDesc' },
];

// Éléments de navigation de l'aperçu (clés i18n) — i === 0 reste l'élément actif.
const PREVIEW_NAV_ITEMS = [
  'themeSettings.previewNavDashboard',
  'themeSettings.previewNavRepairs',
  'themeSettings.previewNavStock',
  'themeSettings.previewNavClients',
];

const ThemeSettingsPage = () => {
  const { api } = useAuth();
  const { t } = useLanguage();
  const [theme, setTheme] = useState(null);
  const [original, setOriginal] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);

  const { applyTheme } = useTheme();

  useEffect(() => {
    const load = async () => {
      try {
        const res = await api.get('/company-settings/theme');
        setTheme(res.data);
        setOriginal(res.data);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    };
    load();
  }, [api]);

  const handleChange = (key, value) => {
    setTheme(prev => ({ ...prev, [key]: value }));
    setSaved(false);
  };

  const applyPreset = (preset) => {
    setTheme(prev => ({ ...prev, primary: preset.primary, secondary: preset.secondary, accent: preset.accent }));
    setSaved(false);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await api.put('/company-settings/theme', theme);
      setTheme(res.data);
      setOriginal(res.data);
      setSaved(true);
      applyTheme(res.data);
      setTimeout(() => setSaved(false), 3000);
    } catch (err) { console.error(err); }
    finally { setSaving(false); }
  };

  const handleReset = () => {
    setTheme({ ...original });
    setSaved(false);
  };

  const applyThemeToDOM = (t) => {
    const root = document.documentElement;
    Object.entries(t).forEach(([key, value]) => {
      if (value && value.startsWith('#')) {
        root.style.setProperty(`--theme-${key}`, value);
      }
    });
  };

  if (loading) return <Layout><div className="flex items-center justify-center h-64"><div className="loading-spinner"></div></div></Layout>;

  const hasChanges = JSON.stringify(theme) !== JSON.stringify(original);

  return (
    <Layout>
      <div className="max-w-4xl mx-auto space-y-6" data-testid="theme-settings-page">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            <div className="w-11 h-11 rounded-2xl bg-gradient-to-br from-pink-500 to-purple-600 flex items-center justify-center shadow-lg shadow-purple-500/20">
              <Palette className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white" data-testid="theme-page-title">{t('themeSettings.title')}</h1>
              <p className="text-sm text-gray-500">{t('themeSettings.subtitle')}</p>
            </div>
          </div>
          <div className="flex items-center gap-2">
            {hasChanges && (
              <button onClick={handleReset} className="px-3 py-2 rounded-xl text-sm bg-white/[0.06] text-gray-400 hover:bg-white/10 hover:text-white transition-all flex items-center gap-2" data-testid="theme-reset-btn">
                <RotateCcw className="w-4 h-4" /> {t('themeSettings.cancel')}
              </button>
            )}
            <button onClick={handleSave} disabled={saving || !hasChanges}
              className={`px-5 py-2.5 rounded-xl text-sm font-semibold transition-all flex items-center gap-2 shadow-lg ${
                saved ? 'bg-emerald-500 text-white shadow-emerald-500/20' :
                hasChanges ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white hover:from-blue-600 hover:to-blue-700 shadow-blue-500/20' :
                'bg-white/[0.06] text-gray-600 cursor-not-allowed'
              }`} data-testid="theme-save-btn">
              {saved ? <><Check className="w-4 h-4" /> {t('themeSettings.saved')}</> : saving ? t('themeSettings.saving') : <><Save className="w-4 h-4" /> {t('themeSettings.save')}</>}
            </button>
          </div>
        </div>

        {/* Presets */}
        <div className="rounded-2xl border border-white/[0.06] p-5" style={{background: 'rgba(16,16,24,0.8)'}}>
          <h2 className="text-sm font-bold text-white mb-4">{t('themeSettings.presetsTitle')}</h2>
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {PRESETS.map(preset => {
              const isActive = theme?.primary === preset.primary && theme?.secondary === preset.secondary;
              return (
                <button key={preset.name} onClick={() => applyPreset(preset)}
                  className={`relative p-3 rounded-xl border transition-all text-center ${
                    isActive ? 'border-white/30 bg-white/[0.08] ring-1 ring-white/20' : 'border-white/[0.06] hover:border-white/20 hover:bg-white/[0.04]'
                  }`} data-testid={`preset-${preset.name.toLowerCase().replace(/\s/g, '-')}`}>
                  <div className="flex justify-center gap-1 mb-2">
                    <div className="w-6 h-6 rounded-full shadow-md" style={{background: preset.primary}}></div>
                    <div className="w-6 h-6 rounded-full shadow-md" style={{background: preset.secondary}}></div>
                    <div className="w-6 h-6 rounded-full shadow-md border border-white/20" style={{background: preset.accent}}></div>
                  </div>
                  <span className="text-[11px] text-gray-400 font-medium">{t(preset.labelKey)}</span>
                  {isActive && <div className="absolute top-1.5 right-1.5 w-4 h-4 rounded-full bg-emerald-500 flex items-center justify-center"><Check className="w-2.5 h-2.5 text-white" /></div>}
                </button>
              );
            })}
          </div>
        </div>

        {/* Custom Colors */}
        <div className="rounded-2xl border border-white/[0.06] p-5" style={{background: 'rgba(16,16,24,0.8)'}}>
          <h2 className="text-sm font-bold text-white mb-4">{t('themeSettings.customTitle')}</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
            {COLOR_FIELDS.map(field => (
              <div key={field.key} className="flex items-center gap-3 p-3 rounded-xl bg-white/[0.03] border border-white/[0.06]">
                <label className="relative shrink-0 cursor-pointer group">
                  <div className="w-12 h-12 rounded-xl shadow-lg border-2 border-white/10 group-hover:border-white/30 transition-all"
                    style={{background: theme?.[field.key] || '#000000'}}></div>
                  <input type="color" value={theme?.[field.key] || '#000000'} onChange={(e) => handleChange(field.key, e.target.value)}
                    className="absolute inset-0 opacity-0 cursor-pointer w-full h-full" data-testid={`color-${field.key}`} />
                </label>
                <div className="min-w-0">
                  <p className="text-sm font-semibold text-white">{t(field.labelKey)}</p>
                  <p className="text-[11px] text-gray-500">{t(field.descKey)}</p>
                  <code className="text-[10px] text-gray-600 font-mono">{theme?.[field.key]}</code>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* Live Preview */}
        <div className="rounded-2xl border border-white/[0.06] p-5" style={{background: 'rgba(16,16,24,0.8)'}}>
          <h2 className="text-sm font-bold text-white mb-4 flex items-center gap-2"><Eye className="w-4 h-4" /> {t('themeSettings.previewTitle')}</h2>
          <div className="rounded-xl border border-white/[0.06] overflow-hidden" style={{background: theme?.sidebar_bg || '#0a0a0f'}}>
            {/* Mock header */}
            <div className="px-4 py-3 flex items-center gap-3" style={{background: theme?.header_bg || '#111'}}>
              <div className="w-8 h-8 rounded-lg" style={{background: theme?.primary || '#dc2626'}}></div>
              <span className="text-sm font-bold" style={{color: theme?.sidebar_text || '#fff'}}>{t('themeSettings.previewCompany')}</span>
              <div className="ml-auto flex gap-2">
                <div className="px-3 py-1.5 rounded-lg text-xs font-bold text-white" style={{background: theme?.primary || '#dc2626'}}>{t('themeSettings.previewPrimaryBtn')}</div>
                <div className="px-3 py-1.5 rounded-lg text-xs font-bold text-black" style={{background: theme?.secondary || '#eab308'}}>{t('themeSettings.previewSecondaryBtn')}</div>
              </div>
            </div>
            {/* Mock content */}
            <div className="p-4 flex gap-4">
              <div className="w-48 space-y-2 shrink-0">
                {PREVIEW_NAV_ITEMS.map((navKey, i) => (
                  <div key={navKey} className={`px-3 py-2 rounded-lg text-xs font-medium transition-all ${i === 0 ? 'text-white' : ''}`}
                    style={{
                      background: i === 0 ? `${theme?.primary || '#dc2626'}22` : 'transparent',
                      color: i === 0 ? theme?.primary || '#dc2626' : (theme?.sidebar_text || '#fff') + '99',
                      borderLeft: i === 0 ? `3px solid ${theme?.primary || '#dc2626'}` : '3px solid transparent'
                    }}>
                    {t(navKey)}
                  </div>
                ))}
              </div>
              <div className="flex-1 space-y-3">
                <div className="flex gap-2">
                  <div className="px-3 py-1 rounded-full text-[10px] font-bold text-white" style={{background: theme?.primary || '#dc2626'}}>{t('themeSettings.previewStatusInProgress')}</div>
                  <div className="px-3 py-1 rounded-full text-[10px] font-bold" style={{background: `${theme?.secondary || '#eab308'}33`, color: theme?.secondary || '#eab308'}}>{t('themeSettings.previewStatusPending')}</div>
                </div>
                <div className="grid grid-cols-3 gap-2">
                  {[theme?.primary, theme?.secondary, theme?.accent].map((c, i) => (
                    <div key={i} className="h-16 rounded-lg border border-white/[0.06] flex items-center justify-center" style={{background: `${c}15`}}>
                      <div className="w-6 h-6 rounded-full" style={{background: c}}></div>
                    </div>
                  ))}
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
};

export default ThemeSettingsPage;
